/* ============================================================================
   House of Veda — cart drawer "You may also like" strip
   ----------------------------------------------------------------------------
   Shell: snippets/hov-cart-recs.liquid, rendered by snippets/minicart.liquid (first paint) and by
   the Handlebars #CartTemplate on every AJAX rebuild of .enj-minicart-ajax.
   Cards: sections/hov-cart-recommendations.liquid, fetched through Shopify's Product
   Recommendations API — one request per seed, because the API takes a single product_id.

   Seeds are the newest products in the cart. Their lists are interleaved (newest seed first) and
   anything already in the cart is dropped. Lists are cached per seed for the life of the page, so a
   rebuild repaints from memory; the network is only used while the drawer is open.
   ========================================================================== */
(function () {
  'use strict';

  var SECTION_ID = 'hov-cart-recommendations';
  var MAX_SEEDS = 3;
  var PER_SEED = 10;        // the API's maximum
  var RETRY_MS = 30000;     // wait before re-asking after a network or server error
  var STALL_MS = 8000;      // an add with no answer by then re-enables the strip and re-reads the cart

  var drawer = document.querySelector('.js-minicart');
  if (!drawer || !window.jQuery) return;

  var routesRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var cache = {};           // seed product id -> [{ id, html }]
  var pending = {};         // seed product id -> in-flight promise
  var retryAt = {};         // seed product id -> earliest retry time
  var chosen = {};          // product id -> variant id picked in the card's select
  var failed = {};          // product id -> 'unavailable' | 'retry'
  var cart = null;          // last /cart.js payload from ajaxCart.afterCartLoad
  var shown = [];           // product ids in the track, in order
  var lastInputs = '';      // seeds (and which of them had loaded) at the last paint
  var sticky = false;       // keep `shown` in place (after an add from the strip itself)
  var stickyNext = false;
  var busyVariant = null;   // one add at a time
  var focusIndex = -1;      // keyboard focus to restore once the drawer has been rebuilt
  var scrollLeft = 0;       // the track is re-created on every rebuild

  // Outside .enj-minicart-ajax so it survives rebuilds, and inside the drawer because the theme's
  // toasts render underneath it.
  var live = document.createElement('div');
  live.className = 'hov-recs__live';
  live.setAttribute('role', 'status');
  drawer.appendChild(live);

  function strip() { return drawer.querySelector('[data-hov-recs]'); }
  function isOpen() { return drawer.classList.contains('active'); }

  // Where and how to ask for recommendations: from the strip, or — while the cart is empty and no
  // strip is in the DOM — from the drawer's Handlebars template, which carries the same shell.
  var recsConfig = null;
  function config() {
    var host = strip();
    if (host) return (recsConfig = { url: host.getAttribute('data-url'), intent: host.getAttribute('data-intent') || 'related' });
    if (recsConfig) return recsConfig;
    var tpl = document.getElementById('CartTemplate');
    var m = tpl && /data-url="([^"]+)"[^>]*data-intent="([^"]*)"/.exec(tpl.innerHTML);
    return m ? (recsConfig = { url: m[1], intent: m[2] || 'related' }) : null;
  }

  function announce(message) {
    live.textContent = '';
    setTimeout(function () { live.textContent = message; }, 50);
  }

  // The catalog lists some products twice under reordered titles ("Organic Gram Flour 500g (Besan)"
  // and "Organic Gram Flour (Besan) 500g"), and Shopify recommends one for the other. Comparing
  // titles as sorted word lists keeps such a twin of a cart item, or of another card, out.
  function titleKey(title) {
    return (String(title || '').toLowerCase().match(/[a-z0-9]+/g) || []).sort().join(' ');
  }

  // Newest line first in both sources. ₹0 lines (free gifts, app add-ons) make poor seeds and are
  // often unpublished products the API answers with a 404, but they still count as "in the cart".
  function cartState() {
    var seeds = [];
    var inCart = [];
    var titles = {};
    function add(id, lineTotal, title) {
      id = String(id || '');
      if (!id) return;
      if (inCart.indexOf(id) === -1) inCart.push(id);
      if (lineTotal > 0 && seeds.length < MAX_SEEDS && seeds.indexOf(id) === -1) seeds.push(id);
      titles[titleKey(title)] = true;
    }
    if (cart) {
      cart.items.forEach(function (item) { add(item.product_id, item.final_line_price, item.product_title); });
    } else {
      // First paint: no payload yet, but the Liquid-rendered drawer carries the same data.
      [].forEach.call(drawer.querySelectorAll('.hov-cart-item[data-product-id]'), function (node) {
        var title = node.querySelector('.hov-cart-item-title');
        add(node.getAttribute('data-product-id'), parseInt(node.getAttribute('data-line-total'), 10) || 0,
          title && title.textContent);
      });
    }
    return { seeds: seeds, inCart: inCart, titles: titles };
  }

  // ── Fetching ─────────────────────────────────────────────────────────────────────────────
  function parse(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    return [].map.call(doc.querySelectorAll('[data-hov-rec]'), function (card) {
      return {
        id: card.getAttribute('data-product-id'),
        key: titleKey(card.getAttribute('data-title')),
        html: card.outerHTML
      };
    });
  }

  function fetchSeed(cfg, id) {
    if (!cfg || cache[id] || pending[id] || Date.now() < (retryAt[id] || 0)) return pending[id];
    var url = cfg.url + '?section_id=' + SECTION_ID +
      '&product_id=' + encodeURIComponent(id) + '&limit=' + PER_SEED +
      '&intent=' + encodeURIComponent(cfg.intent);
    pending[id] = fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        // 404/422: the product isn't published to the online store (typical of app-added gift
        // lines). That won't change during the visit, so remember it as "no recommendations".
        if (res.status === 404 || res.status === 422) return '';
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) { cache[id] = html ? parse(html) : []; })
      .catch(function () { retryAt[id] = Date.now() + RETRY_MS; })
      .then(function () { delete pending[id]; });
    return pending[id];
  }

  // The drawer is off-canvas until it is opened, and most page views never open it.
  function load() {
    var cfg = config();
    if (!cfg || !strip() || !isOpen()) return;
    var jobs = cartState().seeds
      .map(function (id) { return fetchSeed(cfg, id); })
      .filter(Boolean);
    if (jobs.length) Promise.all(jobs).then(paint);
  }

  // ── Painting ─────────────────────────────────────────────────────────────────────────────
  // Always recomputed from the current cart and the current DOM, so a late response can't paint
  // over a newer cart, and a freshly rebuilt drawer simply gets painted again.
  function paint() {
    var host = strip();
    if (!host) return;
    var state = cartState();
    var limit = parseInt(host.getAttribute('data-limit'), 10) || 6;
    var previous = shown.join(',');

    // Interleave the seeds' lists, newest seed first, so every product in the cart contributes.
    var lists = state.seeds.map(function (id) { return cache[id] || []; });
    var byId = {};
    var taken = state.titles;
    var picks = [];
    for (var rank = 0; rank < PER_SEED; rank++) {
      for (var i = 0; i < lists.length; i++) {
        var rec = lists[i][rank];
        if (rec && !byId[rec.id] && !taken[rec.key] && state.inCart.indexOf(rec.id) === -1) {
          byId[rec.id] = rec;
          taken[rec.key] = true;
          picks.push(rec);
        }
      }
    }
    // Cards that are still eligible stay where they were, and newcomers go to the end, both after an
    // add from the strip (so nothing jumps under the shopper's finger when the new seed's list
    // arrives) and on rebuilds that change nothing here, like a quantity change. The list is only
    // re-ranked when the seeds, or the lists loaded for them, change.
    var inputs = state.seeds.map(function (id) { return cache[id] ? id : '-'; }).join(',');
    var keep = sticky || inputs === lastInputs;
    lastInputs = inputs;
    if (keep) {
      picks = shown
        .filter(function (id) { return byId[id]; })
        .map(function (id) { return byId[id]; })
        .concat(picks.filter(function (rec) { return shown.indexOf(rec.id) === -1; }));
    }
    picks = picks.slice(0, limit);
    shown = picks.map(function (rec) { return rec.id; });

    var track = host.querySelector('[data-hov-recs-track]');
    var ids = shown.join(',');
    if (track.getAttribute('data-ids') !== ids) {
      track.innerHTML = picks.map(function (rec) { return rec.html; }).join('');
      track.setAttribute('data-ids', ids);
    }
    host.hidden = !picks.length;
    if (!picks.length) {
      focusIndex = -1;
      return;
    }

    applyStates(host);
    // A re-ranked list starts from its first card; a kept one (a rebuild) keeps its place.
    if (ids !== previous && !keep) scrollLeft = 0;
    track.scrollLeft = Math.min(scrollLeft, track.scrollWidth - track.clientWidth);
    updateArrows(host);

    if (focusIndex > -1) {
      var buttons = track.querySelectorAll('[data-hov-rec-add]');
      var target = buttons[Math.min(focusIndex, buttons.length - 1)];
      focusIndex = -1;
      if (target) target.focus();
    }
  }

  // Cards are re-created from cached HTML on every rebuild, so per-card state is re-applied here.
  function applyStates(host) {
    [].forEach.call(host.querySelectorAll('[data-hov-rec]'), function (card) {
      var id = card.getAttribute('data-product-id');
      var select = card.querySelector('[data-hov-rec-variant]');
      if (select && chosen[id] && select.value !== chosen[id] &&
          select.querySelector('option[value="' + chosen[id] + '"]')) {
        select.value = chosen[id];
        syncVariant(card);
      }

      var button = card.querySelector('[data-hov-rec-add]');
      if (!button.hasAttribute('data-label')) button.setAttribute('data-label', button.textContent);
      var adding = busyVariant !== null && button.getAttribute('data-variant-id') === busyVariant;
      button.disabled = busyVariant !== null || failed[id] === 'unavailable';
      button.setAttribute('aria-busy', adding ? 'true' : 'false');
      button.textContent = adding ? 'Adding…'
        : failed[id] === 'unavailable' ? 'Unavailable'
        : failed[id] === 'retry' ? 'Try again'
        : button.getAttribute('data-label');
    });
  }

  function syncVariant(card) {
    var select = card.querySelector('[data-hov-rec-variant]');
    var option = select.options[select.selectedIndex];
    var compare = card.querySelector('[data-hov-rec-compare]');
    // Both strings were formatted by Liquid's money filter, exactly like the initial price.
    card.querySelector('[data-hov-rec-price]').innerHTML = option.getAttribute('data-price');
    compare.innerHTML = option.getAttribute('data-compare');
    compare.hidden = !option.getAttribute('data-compare');
    card.querySelector('[data-hov-rec-add]').setAttribute('data-variant-id', option.value);
  }

  function updateArrows(host) {
    var track = host.querySelector('[data-hov-recs-track]');
    var end = track.scrollWidth - track.clientWidth - 1;
    host.querySelector('[data-hov-recs-prev]').disabled = track.scrollLeft <= 0;
    host.querySelector('[data-hov-recs-next]').disabled = track.scrollLeft >= end;
  }

  function scrollByCard(arrow) {
    var track = arrow.closest('[data-hov-recs]').querySelector('[data-hov-recs-track]');
    var card = track.firstElementChild;
    if (!card) return;
    var step = card.getBoundingClientRect().width + (parseFloat(getComputedStyle(track).columnGap) || 0);
    track.scrollBy({
      left: arrow.hasAttribute('data-hov-recs-prev') ? -step : step,
      behavior: reduceMotion ? 'auto' : 'smooth'
    });
  }

  // ── Add to cart ──────────────────────────────────────────────────────────────────────────
  function addToCart(button, viaKeyboard) {
    var card = button.closest('[data-hov-rec]');
    var id = card.getAttribute('data-product-id');
    var title = card.getAttribute('data-title');
    var index = [].indexOf.call(card.parentNode.children, card);
    var added = false;

    busyVariant = button.getAttribute('data-variant-id');
    delete failed[id];
    applyStates(strip());
    // Safety net: a dropped connection or a stalled proxy must not leave every Add disabled.
    var mine = busyVariant;
    setTimeout(function () {
      if (busyVariant !== mine) return;
      busyVariant = null;
      var h = strip();
      if (h) applyStates(h);
      if (window.ajaxCart && window.ajaxCart.load) window.ajaxCart.load();
    }, STALL_MS);

    fetch(routesRoot + 'cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(busyVariant), quantity: 1 }] })
    })
      .then(function (res) {
        return res.json().then(function (body) {
          if (res.ok) return body;
          var error = new Error(body.description || body.message || '');
          error.status = res.status;
          throw error;
        });
      })
      .then(function () {
        added = true;
        return fetch(routesRoot + 'cart.js', { credentials: 'same-origin' })
          .then(function (res) { return res.json(); });
      })
      .then(function (payload) {
        busyVariant = null;
        stickyNext = true;
        focusIndex = viaKeyboard ? index : -1;
        announce(title + ' added to cart');
        // The theme's single render path: drawer, header count, the /cart page and cart:updated.
        window.renderAllSurfaces(payload);
      }, function (error) {
        busyVariant = null;
        if (added) {
          // The line is in the cart; only the follow-up read failed. Let the drawer re-read it.
          announce(title + ' added to cart');
          if (window.ajaxCart && window.ajaxCart.load) window.ajaxCart.load();
          return;
        }
        // 422 is Shopify refusing the line: sold out, or a quantity limit reached.
        failed[id] = error.status === 422 ? 'unavailable' : 'retry';
        announce(error.status === 422 && error.message
          ? error.message
          : 'Couldn\'t add ' + title + '. Please try again.');
        var host = strip();
        if (host) applyStates(host);
      });
  }

  // ── Wiring ───────────────────────────────────────────────────────────────────────────────
  // Delegated, because the strip is re-created on every rebuild.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var arrow = e.target.closest('[data-hov-recs-prev], [data-hov-recs-next]');
    if (arrow) {
      scrollByCard(arrow);
      return;
    }
    var button = e.target.closest('[data-hov-rec-add]');
    // detail is 0 for clicks synthesised by Enter/Space, i.e. keyboard activation.
    if (button && !button.disabled) addToCart(button, e.detail === 0);
  });

  document.addEventListener('change', function (e) {
    var select = e.target;
    if (!select.hasAttribute || !select.hasAttribute('data-hov-rec-variant')) return;
    var card = select.closest('[data-hov-rec]');
    chosen[card.getAttribute('data-product-id')] = select.value;
    syncVariant(card);
  });

  // Scroll events don't bubble, but they can be captured on the (static) drawer.
  drawer.addEventListener('scroll', function (e) {
    var track = e.target;
    if (!track.hasAttribute || !track.hasAttribute('data-hov-recs-track')) return;
    scrollLeft = track.scrollLeft;
    updateArrows(track.parentNode);
  }, true);

  // ajax-cart.js (buildCart → cartCallback) and quickview.js (tbuildCart) both trigger this on
  // <body> after every rebuild, with the full /cart.js payload.
  jQuery(document.body).on('ajaxCart.afterCartLoad', function (e, payload) {
    if (payload && payload.items) cart = payload;
    sticky = stickyNext;
    stickyNext = false;
    paint();
    load();
  });

  // There is no "drawer opened" event: every opener toggles .active on .js-minicart.
  new MutationObserver(function () { if (isOpen()) load(); })
    .observe(drawer, { attributes: true, attributeFilter: ['class'] });

  // ajax-cart.js announces the product the moment "Add to cart" is tapped: its recommendations are
  // asked for right away, in parallel with /cart/add.js, so the strip paints together with the new
  // line instead of a round trip later — on phones too, where the drawer only opens afterwards.
  document.addEventListener('hov:cart:adding', function (e) {
    var id = e.detail && e.detail.productId;
    var job = id ? fetchSeed(config(), String(id)) : null;
    if (job) job.then(paint);
  });

  load();
})();
